import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { browserTz } from '../api';
import { HOST_TZ, TimezoneSelect, tzAbbr, tzName } from './TimezoneSelect';

interface Props {
  /** Returns ISO start times between from/to. */
  fetchSlots: (fromIso: string, toIso: string) => Promise<string[]>;
  timezone: string;
  onTimezone: (tz: string) => void;
  maxDaysAhead?: number;
  selected?: string;
  onPick: (iso: string) => void;
}

/** Week-at-a-time date strip with tappable time chips; times shown in the viewer's chosen timezone. */
export function SlotPicker({ fetchSlots, timezone, onTimezone, maxDaysAhead = 60, selected, onPick }: Props) {
  const [weekStart, setWeekStart] = useState(() => DateTime.now().setZone(timezone).startOf('day'));
  const [slots, setSlots] = useState<string[] | null>(null);
  const [error, setError] = useState('');
  const [day, setDay] = useState<string>('');
  const today = DateTime.now().setZone(timezone).startOf('day');
  const lastDay = today.plus({ days: maxDaysAhead });

  useEffect(() => {
    let live = true;
    setSlots(null);
    setError('');
    fetchSlots(weekStart.toUTC().toISO()!, weekStart.plus({ days: 7 }).toUTC().toISO()!)
      .then((s) => live && setSlots(s))
      .catch((e) => live && (setError(e.message), setSlots([])));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart.toMillis()]);

  const byDay = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const s of slots ?? []) {
      const k = DateTime.fromISO(s).setZone(timezone).toISODate()!;
      m.set(k, [...(m.get(k) ?? []), s]);
    }
    return m;
  }, [slots, timezone]);

  const days = Array.from({ length: 7 }, (_, i) => weekStart.plus({ days: i }));
  useEffect(() => {
    if (!slots) return;
    const first = days.find((d) => byDay.has(d.toISODate()!));
    if (!day || !byDay.has(day)) setDay(first?.toISODate() ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byDay]);

  const local = browserTz();

  return (
    <div>
      <div className="row between" style={{ marginBottom: 10 }}>
        <div className="row" style={{ gap: 6 }}>
          <button
            aria-label="Previous week"
            disabled={weekStart <= today}
            onClick={() => setWeekStart(DateTime.max(today, weekStart.minus({ days: 7 })))}
          >
            ‹
          </button>
          <strong>{weekStart.toFormat('LLL d')} – {weekStart.plus({ days: 6 }).toFormat('LLL d')}</strong>
          <button aria-label="Next week" disabled={weekStart.plus({ days: 7 }) > lastDay} onClick={() => setWeekStart(weekStart.plus({ days: 7 }))}>
            ›
          </button>
        </div>
        <div style={{ maxWidth: 260 }}>
          <TimezoneSelect value={timezone} onChange={onTimezone} />
        </div>
      </div>
      <p className="small muted" style={{ margin: '0 0 10px' }}>
        Times shown in <strong>{tzName(timezone)}</strong> ({tzAbbr(timezone)}).{' '}
        {timezone !== HOST_TZ ? (
          <button type="button" className="link-btn small" onClick={() => onTimezone(HOST_TZ)}>Show in Central Time</button>
        ) : local !== HOST_TZ ? (
          <button type="button" className="link-btn small" onClick={() => onTimezone(local)}>Show in my time ({tzName(local)})</button>
        ) : null}
      </p>

      <div className="days" role="listbox" aria-label="Days">
        {days.map((d) => {
          const k = d.toISODate()!;
          const has = byDay.has(k);
          return (
            <button key={k} className={`day ${day === k ? 'on' : ''} ${has ? '' : 'none'}`} disabled={!has} onClick={() => setDay(k)}>
              <small>{d.toFormat('ccc')}</small>
              <span style={{ fontSize: 18, fontWeight: 600 }}>{d.toFormat('d')}</span>
            </button>
          );
        })}
      </div>

      {error && <div className="alert">{error}</div>}
      {slots === null ? (
        <div className="empty"><span className="spin" /></div>
      ) : !byDay.size ? (
        <div className="empty">No open times this week. Try the next week.</div>
      ) : (
        <div className="slots">
          {(byDay.get(day) ?? []).map((s) => (
            <button key={s} className={selected === s ? 'on' : ''} onClick={() => onPick(s)}>
              {DateTime.fromISO(s).setZone(timezone).toFormat('h:mm a')}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
